/**
 * Cube registry — single source of truth for every draggable panel on
 * the console. UI-PHASE-16.A.
 *
 * Each entry binds an id ↔ title ↔ render function ↔ default geometry
 * on the 12-column gridstack. The LayoutManager iterates this list to
 * (1) mount gridstack widgets at boot, (2) re-render content into each
 * widget on every store update, and (3) hydrate saved-layout overrides
 * from localStorage.
 *
 * Adding a new cube:
 *   1. Build the panel as a TemplateResult-returning function.
 *   2. Add an entry below with a UNIQUE id (stable forever — used as
 *      the persistence key).
 *   3. Pick a sensible defaultGeometry. The grid floats, so as long as
 *      x/y/w/h don't collide with another default, you're fine.
 */
import type { TemplateResult } from "lit-html";

import { renderMorningBriefPanel }    from "../components/MorningBrief";
import { renderDailyRitualPanel }     from "../components/DailyRitual";
import { renderWorkersPanel }         from "../components/WorkersPanel";
import {
  renderLaunchAgentsPanel, renderTodaysSlatePanel,
  renderSpendPanel, renderAnomaliesPanel, renderActivityTickerPanel,
} from "../components/LeftRail";

export interface CubeGeometry {
  x: number;       // 0-11
  y: number;       // arbitrary
  w: number;       // 1-12
  h: number;       // 1-N (each row = cellHeight px)
  minW?: number;
  minH?: number;
  /** Lock this cube in place (no move/resize) — used for the mobile
   *  preview when we eventually move it into the grid. */
  locked?: boolean;
}

export interface CubeDef {
  id:    string;
  title: string;
  /** Optional badge text rendered next to the title (e.g. "live") */
  badge?: () => string | null;
  render: () => TemplateResult;
  defaultGeometry: CubeGeometry;
  /** Pinned cubes cannot be removed from the layout — the user might
   *  unintentionally close one if we let them. We don't expose a close
   *  button anyway right now. */
  pinned?: boolean;
}

// Default layout — left column = status pillars, right of those = the
// chart/tab area in the existing center-stage spot. The user can drag
// anything anywhere after the first paint.
//
// Grid is 12 cols. Mobile preview lives OUTSIDE the grid (pinned right
// rail), so the grid effectively spans cols 0-11 of a left+center
// region that's already 420px narrower than the viewport.
//
// Vertical rows are 36px each (set in LayoutManager), so h=10 ≈ 360px.
//
// Tightest stacking:
//   col 0-2:   morning-brief (h3) → daily-ritual (h7) → vps (h3)
//              → workers (h5) → activity-ticker (h12)
//   col 3-11:  tabs (w9 × h14, contains schedule/pipeline/etc.)
//   col 3-5:   spend (w3 × h4) — under tabs
//   col 6-8:   slate (w3 × h4) — under tabs
//   col 9-11:  anomalies + launchagents stacked — under tabs
export const CUBES: CubeDef[] = [
  // Default h values are tuned so each panel's natural content
  // fits without clipping. cellHeight = 36 (set in LayoutManager).
  // Bump if a panel grows out of its cube.
  {
    id: "morning-brief",
    title: "Morning brief",
    render: renderMorningBriefPanel,
    defaultGeometry: { x: 0, y: 0, w: 3, h: 5, minW: 2, minH: 3 },
  },
  {
    id: "daily-ritual",
    title: "Daily ritual",
    render: renderDailyRitualPanel,
    defaultGeometry: { x: 0, y: 5, w: 3, h: 16, minW: 2, minH: 4 },
  },
  {
    id: "workers",
    title: "Scrapers",
    render: renderWorkersPanel,
    defaultGeometry: { x: 0, y: 21, w: 3, h: 13, minW: 2, minH: 5 },
  },
  {
    id: "activity",
    title: "Live activity",
    render: renderActivityTickerPanel,
    defaultGeometry: { x: 0, y: 34, w: 3, h: 14, minW: 2, minH: 5 },
  },

  // ---- Center stage (tab area) — wide widget, drag-aware ----
  // Special: rendered by LayoutManager itself (it knows the active tab
  // + onChange callback). This entry is a placeholder so the layout
  // manager allocates space for it.
  {
    id: "tabs",
    title: "Center stage",
    render: () => ({} as TemplateResult),   // overridden in LayoutManager
    defaultGeometry: { x: 3, y: 0, w: 9, h: 16, minW: 6, minH: 8 },
  },

  // ---- Under the chart — the dead-space release valve ----
  {
    id: "spend",
    title: "Anthropic · today",
    render: renderSpendPanel,
    defaultGeometry: { x: 3, y: 16, w: 3, h: 5, minW: 2, minH: 3 },
  },
  {
    id: "slate",
    title: "Today's slate",
    render: renderTodaysSlatePanel,
    defaultGeometry: { x: 6, y: 16, w: 3, h: 6, minW: 2, minH: 3 },
  },
  {
    id: "anomalies",
    title: "Anomalies",
    render: renderAnomaliesPanel,
    defaultGeometry: { x: 9, y: 16, w: 3, h: 4, minW: 2, minH: 3 },
  },
  {
    id: "launchagents",
    title: "LaunchAgents",
    render: renderLaunchAgentsPanel,
    defaultGeometry: { x: 9, y: 20, w: 3, h: 4, minW: 2, minH: 3 },
  },
];

export function getCube(id: string): CubeDef | undefined {
  return CUBES.find((c) => c.id === id);
}
